import './ui.css';
import { Vector3 } from 'three';
import type { GameContext } from '../core/context';
import type { CrimeType } from '../core/events';
import { lerpAngle, rectContains, type V2 } from '../core/math';
import type { System } from '../core/system';
import { nearestEdge } from '../world/roads';
import type { CityLayout, District, PoiKind } from '../world/types';
import type { GameSettings, GraphicsQuality } from '../save/saveData';
import type { UiSound } from '../audio/audioService';
import { formatMoney } from '../economy/wallet';
import { formatClock } from '../render/mood';
import { touchUi } from '../input/touch';
import { el } from './dom';
import { PadNav } from './padNav';
import { ACTION_REPEAT_S, ACTION_TOAST_S, ACTION_TOASTS, allowed, HELD_TOAST_S, HudAttention, toastPolicy, toastPriority, type ToastPriority } from './attention';
import { failStamp, mph, unitDownToast } from './format';
import { Hud, type HudFrame } from './hud';
import { Minimap, POI_GLYPH as POI_GLYPH_BY_KIND, type MapPlayer, type MapRoute, type MapView } from './minimap';
import { findCar, paintCss, pickOwnCars, pushCarMarkers } from './ownCar';
import { PursuitTracker } from './pursuit';
import { HelpScreen } from './helpScreen';
import {
  ControlsScreen,
  IntroCard,
  PauseScreen,
  ResultScreen,
  SettingsScreen,
  TitleScreen,
  type NavAction,
  type Screen,
  type ScreenHost,
} from './screens';
import type { HudOnline, MissionMeta, MapMarker, MissionIntro, ModuleOverlay, ObjectiveInfo, ResultInfo, ScreenName, SearchArea, ToastKind, UiService } from './uiTypes';
import { FullMapScreen } from './fullMap';
import { buildLegend, hiddenKinds, legendMatches, parseHidden, serializeHidden, type LegendGroup, type LegendItem } from './mapLegend';
import { pickTap, type MapCamera, type TapCandidate, type TapResult } from './mapView';
import { cssSize } from '../render/cssSize';

declare module '../core/hooks' {
  interface BhHooks {
    /** UI snapshot: active screen, HUD read-out. */
    uiState(): Record<string, unknown>;
    /** Drive menus: 'up' | 'down' | 'left' | 'right' | 'confirm' | 'back'. Returns whether a screen consumed it. */
    uiNav(action: string): boolean;
    /** Leave the title screen: 'continue' (default) or 'new'. */
    startGame(kind?: string): void;
    /** Show a result screen: success | failed | busted | wasted. */
    uiResult(kind: string, title?: string): void;
    uiToast(text: string, kind?: string): void;
    uiObjective(title: string, text: string, extra?: Record<string, unknown>): void;
    uiIntro(title: string, client: string, blurb: string, reward: number): void;
    uiScreen(name: string): void;
    /** Push the online HUD pieces by hand (screenshots / layout checks without a server): a `HudOnline` patch. */
    uiHudOnline(patch: Record<string, unknown>): void;
  }
}

const KEY_NAV: Record<string, NavAction> = {
  ArrowUp: 'up',
  KeyW: 'up',
  ArrowDown: 'down',
  KeyS: 'down',
  ArrowLeft: 'left',
  KeyA: 'left',
  ArrowRight: 'right',
  KeyD: 'right',
  Enter: 'confirm',
  Space: 'confirm',
  NumpadEnter: 'confirm',
  Escape: 'back',
  Backspace: 'back',
};

const SKIP_TITLE_PARAMS = ['play', 'debug', 'skip', 'nomenu'];
/** What the constable shouts at a first slip in front of him (heat `policeWarning`), by offence. */
const WARNING_LINES: Partial<Record<CrimeType, readonly string[]>> = {
  vehicleDamage: ['Oi! Watch it!', 'Mind how you go, sunshine!', 'Steady! I saw that.'],
  pedHit: ['Oi! Watch where you’re going!', 'Easy! There’s people on that pavement!'],
  hitPolice: ['Oi! That’s a police car, that is!', 'Careful! You want a night in the cells?'],
  recklessDriving: ['Slow it down! This ain’t a racetrack!', 'Oi! Ease off, you lunatic!'],
};

/** The police radio when a unit chasing you crashes out ("laid on the tarmac", docs/POLICE.md §14): lost him, or not yet. */
const UNIT_DOWN_LINES = {
  lost: ['Unit down! We’ve lost him, Sarge!', 'We’re off the road — anyone got eyes on him?', 'Car’s in the kerb. He’s away!'],
  still: ['Unit down! Somebody stay on him!', 'Bloody hell — we’ve gone into the wall! Keep after him!', 'We’re out of it. Over to you, two-four!'],
} as const;

/**
 * The police radio while they work (docs/POLICE-AI-POLISH.md #5): the search starting, a caller putting
 * the suspect somewhere, a roadblock going up, a re-sighting. `%s` is the street or district.
 */
const RADIO_LINES = {
  search: ['Lost him near %s. All units, spread out and search.', 'No eyes on him. Last seen %s — sweep the area.', 'Suspect’s gone to ground round %s. Keep looking.'],
  tip: ['Caller says he’s on %s. Units, close in.', 'Member of the public has him on %s. Get round there.', 'Sighting on %s from a passer-by. Move!'],
  roadblock: ['Roadblock going up on %s. Hold the line.', 'Two cars across %s — he’ll have to come through us.', 'Block’s in place on %s. Nobody gets past.'],
  resight: ['There he is! %s, all units!', 'Got him again on %s. Go, go!', 'Eyes on. He’s on %s — after him!'],
} as const;
/** Seconds between two police radio lines (a burst of tip-offs is one line). */
const RADIO_GAP = 4;
/** localStorage: the map key's switched-off groups (mapLegend.ts). */
const MAP_HIDE_KEY = 'bh-map-hide';
/** The route line on the maps: the arrows' colours (onboarding/routeView.ts), as CSS. */
const ROUTE_JOB_COLOUR = '#e0533a';
const ROUTE_MARK_COLOUR = '#c8a2ea';

interface QueuedResult {
  info: ResultInfo;
  resolve: (choice?: string) => void;
  /** The sheet on screen (the "what came of it" line is filled in a frame after it comes up). */
  screen?: ResultScreen;
  afterSet?: boolean;
}

export function createUiSystem(): System {
  let ctx: GameContext;
  let hud: Hud | null = null;
  let minimap: Minimap | null = null;
  let root: HTMLElement | null = null;
  let layout: CityLayout | null = null;
  let screenName: ScreenName = 'boot';
  let overlay: Screen | null = null;
  let overlayName: ScreenName = 'boot';
  let titleScreen: TitleScreen | null = null;
  /** "Play online" is fetching the online client (openOnline). */
  let onlineLoading = false;
  let intro: IntroCard | null = null;
  let fullMap: FullMapScreen | null = null;
  let legendTimer = 0;
  let poiKinds: Set<PoiKind> | null = null;
  const tapScratch: TapCandidate[] = [];
  const fullPlayer: MapPlayer = { x: 0, z: 0, heading: 0, tint: null };
  const mapPlayer: MapPlayer = { x: 0, z: 0, heading: 0, tint: null };
  let started = false;
  let firstUpdate = true;
  let time = 0;
  let districtId = '';
  let streetTimer = 0;
  let streetName = '';
  let districtName = '';
  let seenTimer = 0;
  let seenCache = false;
  let hitFlashCooldown = 0;
  let warnings = 0;
  let unitDowns = 0;
  let radioAt = -99;
  const radioLine: Record<keyof typeof RADIO_LINES, number> = { search: 0, tip: 0, roadblock: 0, resight: 0 };
  let heatPhase = 'none';
  let toastSoundCooldown = 0;
  let viewHeading = Math.PI;
  let viewRadius = 70;
  let searchOverride: SearchArea | null | undefined;
  let letterboxOn = false;
  let hudVisible = true;
  let slowedForResult = false;
  const results: QueuedResult[] = [];
  let activeResult: QueuedResult | null = null;
  /** Toasts that arrived while a result sheet had the screen: shown once it is dismissed (the last few). */
  const heldToasts: { text: string; kind: ToastKind; seconds?: number }[] = [];
  const HELD_TOASTS_MAX = 3;
  /** The HUD's attention (src/ui/attention.ts): calm / busy / action, and what each piece may do in each. */
  const attention = new HudAttention();
  let sinceCombat = Infinity;
  /** Toasts held back during action: shown once it is over, if they are still fresh (the last few). */
  const actionToasts: { text: string; kind: ToastKind; seconds?: number; at: number; nudge: boolean }[] = [];
  /** Urgent lines said during action, and when: the same one is said once in ACTION_REPEAT_S. */
  const urgentSaid = new Map<string, number>();
  let pendingFail: { id: string; reason: string; t: number } | null = null;
  const missionMeta = new Map<string, MissionMeta>();
  const markerSources = new Set<(out: MapMarker[]) => void>();
  const markers: MapMarker[] = [];
  /** The car the player was in last (ownCar.ts: "the last car you drove" on the map). */
  let lastDriven = -1;
  /** The arrow's paint while driving, as CSS (made once per paint, not per frame). */
  let tintPaint = -1;
  let tintCss = '';
  /** The key's switches: groups off the map (localStorage MAP_HIDE_KEY). */
  let hiddenGroups = new Set<LegendGroup>();
  /** The route line as the maps draw it (points borrowed from the route service's path; no copy). */
  const routeLine: MapRoute = { xs: new Float32Array(0), zs: new Float32Array(0), count: 0, colour: '' };
  const tracker = new PursuitTracker();
  const pad = new PadNav();
  const projVec = new Vector3();
  const offs: (() => void)[] = [];
  let keyHandler: ((e: KeyboardEvent) => void) | null = null;
  let uiMs = 0;
  let shakeScale = 1;
  let shakePatched = false;
  let mobileTierSet = false;
  const playerPos: V2 = { x: 0, z: 0 };
  /** The job's objective (missions) wins over the "what next" line (onboarding) on the clipping. */
  let jobObjective: ObjectiveInfo | null = null;
  let idleObjective: ObjectiveInfo | null = null;
  /** Another module's overlay (the book) and how to make it let go of the screen. */
  let moduleOverlay: { s: ModuleOverlay; close: () => void } | null = null;

  const audio = () => ctx.services.tryGet('audio');
  const sfx = (s: UiSound): void => audio()?.ui(s);

  // ------------------------------------------------------------------ settings

  const host: ScreenHost = {
    sound: sfx,
    settings: () => ctx.services.get('save').settings,
    setSettings: (patch) => ctx.services.get('save').updateSettings(patch),
    bindings: () => ctx.input.bindings,
    qualityTier: () => ctx.services.tryGet('renderPipeline')?.quality ?? null,
    touch: () => touchUi.enabled,
    timeOfDay: () => {
      const t = ctx.services.tryGet('timeOfDay');
      if (!t) return 'DUSK';
      if (t.shard) return `CITY · ${formatClock(t.hour)}`;
      return t.setting === 'auto' ? `AUTO · ${formatClock(t.hour)}` : t.setting.toUpperCase();
    },
    cycleTimeOfDay: (dir) => {
      const t = ctx.services.tryGet('timeOfDay');
      if (!t || t.shard) return; // online the city keeps the clock
      const ids = t.options.map((o) => o.id);
      t.set(ids[(ids.indexOf(t.setting) + dir + ids.length) % ids.length]!);
    },
    get tips() {
      return ctx.services.tryGet('onboarding')?.tipsSetting;
    },
  };

  /** Make a module overlay (the book) let go of the screen before something else takes it. */
  function forceCloseModule(): void {
    const m = moduleOverlay;
    if (!m) return;
    moduleOverlay = null;
    if (overlay === m.s) {
      overlay = null;
      overlayName = 'hud';
      screenName = 'hud';
    }
    m.close();
  }

  function applySettings(s: GameSettings): void {
    const a = audio();
    if (a) {
      a.setVolume('master', s.master);
      a.setVolume('music', s.music);
      a.setVolume('sfx', s.sfx);
    }
    shakeScale = s.cameraShake;
    const rig = ctx.services.tryGet('cameraRig');
    if (rig) rig.distanceScale = s.cameraZoom;
    applyGraphics(s.graphics);
  }

  function applyGraphics(q: GraphicsQuality): void {
    // The tier (shadows, post-processing) belongs to the render module; 'auto' hands it back to its
    // frame-rate adaptation.
    const pipeline = ctx.services.tryGet('renderPipeline');
    // Phones and tablets (docs/MOBILE.md): AUTO starts at MEDIUM and never climbs to HIGH. The pixel ratio
    // belongs to the render module too (src/render/resolution.ts): on touch, AUTO steps it 1.0 → 1.5 on
    // the measured frame time and LOW pins it at 1.0; it only needs to know the device is a touch one.
    const mobile = touchUi.enabled;
    pipeline?.setDeviceProfile?.({ touch: mobile });
    if (mobile && q === 'auto' && !mobileTierSet) {
      mobileTierSet = true;
      pipeline?.setQuality?.('medium');
    }
    pipeline?.setAutoCeiling?.(mobile ? 'medium' : 'high');
    pipeline?.setQuality?.(q);
  }

  /** Camera shake scale: wrap the rig's `shake` once (the rig is owned by game-director; we only scale its input). */
  function patchShake(): void {
    const rig = ctx.services.tryGet('cameraRig');
    if (!rig || shakePatched) return;
    const orig = rig.shake.bind(rig);
    rig.shake = (amount: number) => orig(amount * shakeScale);
    shakePatched = true;
  }

  // ------------------------------------------------------------------ screens

  function setOverlay(s: Screen | null, name: ScreenName): void {
    overlay?.dispose();
    overlay = s;
    overlayName = name;
    screenName = s ? name : started ? 'hud' : 'title';
  }

  function showTitle(): void {
    if (!root) return;
    forceCloseModule();
    ctx.state.mode = 'title';
    started = false;
    hud?.setVisible(false);
    ctx.services.get('player').controls.acquire('title');
    const save = ctx.services.get('save');
    titleScreen?.dispose();
    titleScreen = new TitleScreen(root, host, {
      hasProgress: save.hasProgress,
      onContinue: () => startGame('continue'),
      onNewGame: () => startGame('new'),
      onSettings: () => openSettings(),
      onControls: () => openControls(),
      onHelp: () => openHelp(),
      ...(ctx.services.has('onlineUi') || ctx.services.has('onlineClient') ? { onOnline: () => openOnline() } : {}),
    });
    setOverlay(titleScreen, 'title');
  }

  /**
   * Online sign-in / city picker (src/online-ui) as a title-screen overlay; Esc comes back here. The online
   * client is fetched first if it is not in yet (net/onlineBoot.ts); the title stays up meanwhile, and the
   * sign-in only opens if it still is.
   */
  function openOnline(): void {
    const client = ctx.services.tryGet('onlineClient');
    if (root && client && !client.loaded) {
      if (onlineLoading) return;
      onlineLoading = true;
      sfx('confirm');
      const title = titleScreen;
      client.load().then(
        () => {
          onlineLoading = false;
          if (title && titleScreen === title && overlay === title) showSignIn();
        },
        (e: unknown) => {
          onlineLoading = false;
          console.warn('online client failed to load', e);
        },
      );
      return;
    }
    sfx('confirm');
    showSignIn();
  }

  function showSignIn(): void {
    const online = ctx.services.tryGet('onlineUi');
    if (!root || !online) return;
    titleScreen = null;
    const s = online.createSignInScreen(root, host, {
      onBack: () => showTitle(),
      onJoined: () => startGame('online'),
    });
    setOverlay(s, 'title');
  }

  function startGame(kind: 'continue' | 'new' | 'online'): void {
    const save = ctx.services.get('save');
    if (kind === 'new') save.newGame();
    sfx('confirm');
    audio()?.unlock();
    if (titleScreen) {
      const t = titleScreen;
      titleScreen = null;
      overlay = null;
      t.dispose();
    } else if (kind === 'online' && overlay) {
      const o = overlay;
      overlay = null;
      o.dispose();
    }
    const station = save.settings.radioStation;
    if (station >= 0) audio()?.radio.tune(station);
    overlayName = 'hud';
    screenName = 'hud';
    started = true;
    ctx.state.mode = 'playing';
    // Safety net: a fresh start (continue / new / online) leaves nothing holding the controls.
    ctx.services.get('player').controls.clear();
    const rig = ctx.services.tryGet('cameraRig');
    rig?.setOverride(null);
    rig?.snap();
    hud?.setVisible(hudVisible);
    districtId = '';
    if (kind === 'new') hud?.toast('New start. Forty quid and a bad reputation.', 'info', 4.5);
    else if (kind === 'online') hud?.toast(`Online in ${ctx.services.tryGet('net')?.shard?.name ?? 'Kessmouth'}. Mind who you trust.`, 'info', 4);
    else hud?.toast('Back on the manor.', 'info', 3);
  }

  function openSettings(): void {
    if (!root) return;
    const hiddenRoot = overlay?.root;
    if (hiddenRoot) hiddenRoot.hidden = true;
    const prev = overlay;
    const prevName = overlayName;
    const s = new SettingsScreen(root, host, () => {
      s.dispose();
      overlay = prev;
      overlayName = prevName;
      screenName = prevName;
      if (prev) prev.root.hidden = false;
    });
    // keep `prev` alive underneath; the settings screen is the active overlay
    overlay = s;
    overlayName = 'settings';
    screenName = 'settings';
  }

  function openControls(): void {
    if (!root) return;
    const prev = overlay;
    const prevName = overlayName;
    if (prev) prev.root.hidden = true;
    const s = new ControlsScreen(root, host, () => {
      s.dispose();
      overlay = prev;
      overlayName = prevName;
      screenName = prevName;
      if (prev) prev.root.hidden = false;
    });
    overlay = s;
    overlayName = 'controls';
    screenName = 'controls';
  }

  /** How to play: stacked over whatever is up (title / pause), like Controls. */
  function openHelp(): void {
    if (!root) return;
    const prev = overlay;
    const prevName = overlayName;
    if (prev) prev.root.hidden = true;
    const s = new HelpScreen(root, host, () => {
      s.dispose();
      overlay = prev;
      overlayName = prevName;
      screenName = prevName;
      if (prev) prev.root.hidden = false;
    });
    overlay = s;
    overlayName = 'help';
    screenName = 'help';
  }

  function openPause(): void {
    if (!root || !started) return;
    forceCloseModule();
    const save = ctx.services.get('save');
    const economy = ctx.services.tryGet('economy');
    const mins = Math.floor(save.data.stats.playTime / 60);
    const p = new PauseScreen(root, host, {
      onResume: () => ctx.hooks.call('pause', false),
      onSettings: () => openSettings(),
      onControls: () => openControls(),
      onHelp: () => openHelp(),
      ...(ctx.services.tryGet('feedback') ? { onReport: () => ctx.services.tryGet('feedback')?.open() } : {}),
      // The profile page (src/profile) wants the HUD back under it: un-pause (the pause screen goes), then open.
      ...(ctx.services.tryGet('profile')
        ? {
            onProfile: () => {
              ctx.hooks.call('pause', false);
              ctx.services.tryGet('profile')?.open();
            },
          }
        : {}),
      onQuit: () => {
        save.flush();
        ctx.services.tryGet('net')?.disconnect('quit');
        window.location.reload();
      },
      stats: () => [
        { label: 'Cash', value: `£${formatMoney(economy?.balance ?? ctx.state.money)}` },
        { label: 'Jobs done', value: String(save.data.completedMissions.length) },
        { label: 'Motors nicked', value: String(save.data.stats.carsStolen) },
        { label: 'Times nicked', value: String(save.data.stats.timesBusted) },
        { label: 'Times wasted', value: String(save.data.stats.timesWasted) },
        { label: 'Time on the manor', value: `${mins} min` },
      ],
    });
    setOverlay(p, 'pause');
    hud?.setPaused(true);
  }

  function closePause(): void {
    if (overlayName === 'pause' || overlayName === 'settings' || overlayName === 'controls' || overlayName === 'help') {
      // Dispose any stacked screens (settings over pause keeps pause hidden underneath).
      root?.querySelectorAll('.bh-pause, .bh-settings, .bh-controls, .bh-help').forEach((n) => n.remove());
      overlay = null;
      overlayName = 'hud';
      screenName = 'hud';
    }
    hud?.setPaused(false);
  }

  function toggleFullMap(): void {
    if (!root || !started || ctx.state.mode !== 'playing' || !minimap) return;
    if (fullMap) {
      closeFullMap();
      return;
    }
    forceCloseModule();
    if (overlay) return;
    const d = districtName || layout?.name || '';
    gatherMarkers(ctx.state.heat);
    const player = ctx.services.get('player');
    fullMap = new FullMapScreen(
      root,
      {
        close: closeFullMap,
        sound: (s) => sfx(s),
        player: () => player.pos,
        pick: mapPick,
        route: mapRoute,
        clearRoute: mapClearRoute,
        markLabel: () => ctx.services.tryGet('onboarding')?.waypoint?.label ?? null,
        locate: locateOnMap,
        hasMark: () => !!ctx.services.tryGet('onboarding')?.waypoint,
        car: carToFind,
        hidden: () => hiddenGroups,
        setHidden: setHiddenGroups,
      },
      d,
      mapLegend(),
      touchUi.enabled,
      layout ? layout.bounds : { minX: -100, minZ: -100, maxX: 100, maxZ: 100 },
    );
    legendTimer = 0;
    overlay = fullMap;
    overlayName = 'map';
    screenName = 'map';
    sfx('whoosh');
  }

  function closeFullMap(): void {
    if (!fullMap) return;
    fullMap.dispose();
    fullMap = null;
    if (overlayName === 'map') {
      overlay = null;
      overlayName = 'hud';
      screenName = 'hud';
    }
    sfx('back');
  }

  /** Your car for the map's Find my car (ownCar.ts): the motor, else the last car you drove; `inIt` while driving it. */
  function carToFind(): { x: number; z: number; label: string; glyph?: string; inIt: boolean } | null {
    const player = ctx.services.get('player');
    const v = player.vehicle;
    if (v && player.mode === 'driving') return { x: v.pos.x, z: v.pos.z, label: `Your car — ${v.spec.displayName}`, inIt: true };
    const m = findCar(markers);
    if (!m) return null;
    return m.glyph ? { x: m.x, z: m.z, label: m.label ?? 'Your car', glyph: m.glyph, inIt: false } : { x: m.x, z: m.z, label: m.label ?? 'Your car', inIt: false };
  }

  /** The key's switches changed: both maps drop those kinds; remembered across sessions. */
  function setHiddenGroups(groups: ReadonlySet<LegendGroup>): void {
    hiddenGroups = new Set(groups);
    minimap?.setHidden(hiddenKinds(hiddenGroups));
    try {
      localStorage.setItem(MAP_HIDE_KEY, serializeHidden(hiddenGroups));
    } catch {
      /* private mode */
    }
  }

  /** The route the arrows follow, as a line for the maps (null: no arrows up). */
  function currentRoute(): MapRoute | null {
    const r = ctx.services.tryGet('route');
    const path = r?.path;
    if (!path || path.count < 2) return null;
    routeLine.xs = path.xs;
    routeLine.zs = path.zs;
    routeLine.count = path.count;
    routeLine.colour = r!.kind === 'mark' ? ROUTE_MARK_COLOUR : ROUTE_JOB_COLOUR;
    return routeLine;
  }

  /** The full map's key: only what is on the map right now (src/ui/mapLegend.ts). */
  function mapLegend(): LegendItem[] {
    const wp = ctx.services.tryGet('onboarding')?.waypoint ?? null;
    return buildLegend(markers, poiKinds ?? (poiKinds = new Set(layout?.pois.map((p) => p.kind) ?? [])), wp);
  }

  /**
   * What a tap on the full map means: on your flag "clear the route"; on a place (a shop, someone with work,
   * the job, an event) that place by name; anywhere else the spot, named by its street. The full map then
   * asks (Route here / Clear route) before anything changes.
   */
  function mapPick(sx: number, sy: number, cam: MapCamera): TapResult | null {
    const ob = ctx.services.tryGet('onboarding');
    if (!ob || !minimap) return null;
    return pickTap(cam, sx, sy, minimap.tapCandidates(markers, tapScratch), ob.waypoint, spotName);
  }

  /**
   * Route here: the place becomes your mark (the book's waypoint), so the arrows on the road and the screen
   * pointer lead there — during a job too, until you get there or clear it (src/onboarding/routeGoal.ts).
   */
  function mapRoute(p: { x: number; z: number; label: string }): void {
    ctx.services.tryGet('onboarding')?.setWaypoint({ x: p.x, z: p.z, label: p.label, from: 'map' });
    sfx('confirm');
    const job = !!ctx.services.tryGet('missions')?.active;
    service.toast(job ? `Route set: ${p.label}. Your job waits on the map till you get there.` : `Route set: ${p.label}. Follow the arrows on the road.`, 'info', 2.6);
  }

  function mapClearRoute(): void {
    ctx.services.tryGet('onboarding')?.setWaypoint(null);
    sfx('back');
    service.toast('Route cleared.', 'info', 1.8);
  }

  /** One police radio line, named by street, at most one every RADIO_GAP seconds. */
  function policeRadio(kind: keyof typeof RADIO_LINES, x: number, z: number): void {
    const now = ctx.state.time;
    if (now - radioAt < RADIO_GAP) return;
    radioAt = now;
    const lines = RADIO_LINES[kind];
    const line = lines[radioLine[kind]++ % lines.length]!;
    service.say('Police radio', line.replace('%s', spotName(x, z)), 2.6);
  }

  /** A spot with no place on it is named by its street (or district). */
  function spotName(x: number, z: number): string {
    if (!layout) return 'that spot';
    const ne = nearestEdge(layout, { x, z });
    if (ne && ne.distance < 25 && ne.edge.name) return ne.edge.name;
    const d = districtAt({ x, z });
    return d ? d.name : 'that spot';
  }

  /** The nearest marker of a key line to (x, z) — the key's "show me where". */
  function locateOnMap(it: LegendItem, x: number, z: number): { x: number; z: number } | null {
    if (it.kind === 'you') return ctx.services.get('player').pos;
    if (it.kind === 'mark') return ctx.services.tryGet('onboarding')?.waypoint ?? null;
    let best: { x: number; z: number } | null = null;
    let bd = Infinity;
    const consider = (m: MapMarker): void => {
      if (!legendMatches(it, m)) return;
      const d = (m.x - x) ** 2 + (m.z - z) ** 2;
      if (d < bd) {
        bd = d;
        best = m;
      }
    };
    for (const m of markers) consider(m);
    if (it.kind === 'poi' && layout) {
      for (const p of layout.pois) {
        const glyph = POI_GLYPH_BY_KIND[p.kind];
        if (glyph && glyph === it.glyph) consider({ x: p.pos.x, z: p.pos.z, kind: 'poi', glyph });
      }
    }
    return best;
  }

  // ------------------------------------------------------------------ results

  function pumpResults(): void {
    if (activeResult || !root || results.length === 0) return;
    forceCloseModule();
    const next = results.shift()!;
    intro?.leave();
    activeResult = next;
    const info = next.info;
    const player = ctx.services.get('player');
    // Slow the world and take the wheel while the sheet is up. Busted/wasted run on the police
    // module's own respawn timer, so the world keeps normal speed for those.
    const outcomeKind = info.kind === 'busted' || info.kind === 'wasted';
    // Online the world is shared and keeps real time: never slow it for a result sheet.
    if (!outcomeKind && !ctx.services.tryGet('net')?.online) {
      ctx.loop.timeScale = 0.25;
      slowedForResult = true;
    }
    player.controls.acquire('resultSheet');
    const save = ctx.services.get('save');
    save.flush();
    const screen = new ResultScreen(root, info, host, (choice) => {
      const done = activeResult;
      activeResult = null;
      if (overlay === screen) {
        overlay = null;
        overlayName = 'hud';
        screenName = 'hud';
      }
      screen.dispose();
      if (slowedForResult && results.length === 0) {
        ctx.loop.timeScale = 1;
        slowedForResult = false;
      }
      const economy = ctx.services.tryGet('economy');
      if (outcomeKind && economy?.outcomePending) economy.respawn(info.kind as 'busted' | 'wasted');
      // Still mid-sequence (respawn pending): the outcome's own reason keeps holding the controls.
      player.controls.release('resultSheet');
      done?.resolve(choice);
      pumpResults();
      // The post that came while the sheet was up (the next job, the bleeper) lands now, not on the sheet.
      if (!activeResult) for (const t of heldToasts.splice(0)) service.toast(t.text, t.kind, t.seconds);
    });
    next.screen = screen;
    if (fullMap) closeFullMap();
    overlay = screen;
    overlayName = 'result';
    screenName = 'result';
  }

  /**
   * The line under a job's tally saying what it opened up ("Mags has more for you — Wet Paper, Phone Box,
   * The Parade"): the next story job on the board, else nothing. It used to arrive as a toast on top of the
   * sheet. Read a frame after the sheet comes up: when `missionCompleted` fires the save hasn't recorded
   * the job yet, so the board still shows the job just done as the next one.
   */
  function whatCameOfIt(): string | null {
    const board = ctx.services.tryGet('missions')?.board() ?? [];
    const next = board.find((j) => j.kind === 'story' && j.available && !j.completed);
    if (!next) return null;
    const who = next.client.trim().split(/\s+/)[0] ?? next.client;
    const where = next.giver.name.replace(/\s*\([^)]*\)\s*$/, '');
    return `Next job: “${next.title}” for ${who} — go to ${where}.`;
  }

  function enqueueResult(info: ResultInfo): Promise<string | undefined> {
    return new Promise<string | undefined>((resolve) => {
      if (!root) return resolve(undefined);
      results.push({ info, resolve });
      pumpResults();
    });
  }

  // ------------------------------------------------------------------ service

  const service: UiService = {
    get screen() {
      return screenName;
    },
    setObjective(o: ObjectiveInfo | null) {
      jobObjective = o;
      hud?.setObjective(o ?? idleObjective);
    },
    setIdleObjective(o: ObjectiveInfo | null) {
      idleObjective = o;
      if (!jobObjective) hud?.setObjective(o);
    },
    openOverlay(s, _name, onForceClose) {
      if (!root || !started || activeResult || ctx.state.mode !== 'playing') return false;
      if (fullMap) closeFullMap();
      if (overlay) return false;
      overlay = s;
      overlayName = 'book';
      screenName = 'book';
      moduleOverlay = { s, close: onForceClose };
      return true;
    },
    closeOverlay(s) {
      if (moduleOverlay?.s === s) moduleOverlay = null;
      if (overlay === s) {
        overlay = null;
        overlayName = 'hud';
        screenName = 'hud';
      }
    },
    showScreen(name) {
      forceCloseModule();
      if (name === 'map') {
        if (!fullMap) toggleFullMap();
      } else if (!started) {
        if (name === 'help') openHelp();
        else openSettings();
      } else {
        ctx.hooks.call('pause', true);
        if (name === 'help') openHelp();
        else openSettings();
      }
    },
    get free() {
      return started && !overlay && !activeResult && ctx.state.mode === 'playing';
    },
    get saying() {
      return !!hud?.saying;
    },
    get lawUp() {
      return !!hud?.lawUp;
    },
    get markers() {
      return markers;
    },
    get cinematic() {
      return letterboxOn || !hudVisible;
    },
    hudOnline(patch) {
      hud?.setOnline(patch);
    },
    get attention() {
      return attention.mode;
    },
    toast(text: string, kind: ToastKind = 'info', seconds?: number, priority?: ToastPriority) {
      // Action first (src/ui/attention.ts): in a chase, a fight or at speed only the urgent lines get through,
      // one at a time, short, once; the rest wait for it to be over, the pager's nudges merged into the newest.
      const p = toastPriority(kind, priority);
      const policy = toastPolicy(p, attention.mode);
      if (policy !== 'show') {
        if (policy === 'merge') {
          const old = actionToasts.findIndex((t) => t.nudge);
          if (old >= 0) actionToasts.splice(old, 1);
        }
        actionToasts.push({ text, kind, seconds, at: time, nudge: policy === 'merge' });
        while (actionToasts.length > HELD_TOASTS_MAX) actionToasts.shift();
        return;
      }
      let max: number | undefined;
      if (attention.mode === 'action') {
        const said = urgentSaid.get(text);
        if (said !== undefined && time - said < ACTION_REPEAT_S) return;
        urgentSaid.set(text, time);
        seconds = Math.min(seconds ?? ACTION_TOAST_S, ACTION_TOAST_S);
        max = ACTION_TOASTS;
      }
      if (activeResult) {
        // A result sheet owns the screen: toasts wait for it (the last few, newest kept).
        heldToasts.push({ text, kind, seconds });
        while (heldToasts.length > HELD_TOASTS_MAX) heldToasts.shift();
        return;
      }
      hud?.toast(text, kind, seconds, max);
      if (toastSoundCooldown <= 0) {
        if (kind === 'bad') sfx('denied');
        else if (kind !== 'cash') audio()?.stinger('toast');
        toastSoundCooldown = 0.35;
      }
    },
    say: (speaker, text, seconds) => {
      hud?.say(speaker, text, seconds);
      ctx.events.emit('dialogueShown', { speaker, seconds: seconds ?? 4 });
    },
    showMissionIntro(m: MissionIntro) {
      return new Promise<void>((resolve) => {
        if (!root) return resolve();
        intro?.dispose();
        intro = new IntroCard(root, m, host, 6.5, () => {
          intro = null;
          resolve();
        });
      });
    },
    dismissIntro() {
      intro?.leave();
    },
    showResult: (r: ResultInfo) => enqueueResult(r),
    addMarkerSource(fn) {
      markerSources.add(fn);
      return () => markerSources.delete(fn);
    },
    setSearchArea(a) {
      searchOverride = a;
    },
    letterbox(on) {
      letterboxOn = on;
      hud?.letterbox(on);
    },
    setHudVisible(v) {
      hudVisible = v;
      hud?.setVisible(v && started);
    },
    registerMission(id, meta) {
      missionMeta.set(id, meta);
    },
  };

  // Headless / no-DOM: same API, does nothing visible.
  const nullService: UiService = {
    screen: 'boot',
    attention: 'calm',
    setObjective: () => undefined,
    toast: () => undefined,
    say: () => undefined,
    showMissionIntro: () => Promise.resolve(),
    dismissIntro: () => undefined,
    showResult: () => Promise.resolve(undefined),
    addMarkerSource: () => () => undefined,
    setSearchArea: () => undefined,
    letterbox: () => undefined,
    setHudVisible: () => undefined,
    registerMission: () => undefined,
    setIdleObjective: () => undefined,
    openOverlay: () => false,
    closeOverlay: () => undefined,
    showScreen: () => undefined,
    free: false,
    saying: false,
    lawUp: false,
    markers: [],
    cinematic: false,
    hudOnline: () => undefined,
  };

  // ------------------------------------------------------------------ input routing

  function route(a: NavAction): boolean {
    if (!overlay) return false;
    return overlay.onNav(a);
  }

  function installKeys(): void {
    keyHandler = (e: KeyboardEvent): void => {
      if (!overlay) return;
      if ((e.target as Element | null)?.closest?.('[data-bh-text-entry]')) return; // a form over the menu (src/feedback) keeps its keys
      // We may swallow this key below, which would starve the audio module's own unlock listener.
      audio()?.unlock();
      if (e.repeat &&(e.code === 'Enter' || e.code === 'Space' || e.code === 'Escape')) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      if (moduleOverlay && moduleOverlay.s === (overlay as unknown) && moduleOverlay.s.onMenuKey?.(e)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      let a: NavAction | undefined = KEY_NAV[e.code];
      if (overlayName === 'map' && (e.code === 'KeyM' || e.code === 'Tab')) a = 'back';
      if (overlayName === 'result' && e.code === 'KeyE') a = 'confirm';
      if (overlayName === 'book' && e.code === 'KeyI') a = 'back';
      if (!a) {
        // Swallow stray gameplay keys while a blocking screen is up (result), pass the rest.
        if (overlayName === 'result') {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
        return;
      }
      // Esc on the pause menu itself belongs to the game (it un-pauses); everywhere else we consume it.
      if (a === 'back' && overlayName === 'pause') return;
      // On the title / fullmap the "wasd" aliases are only navigation — fine. On the HUD we never get here.
      if (route(a)) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener('keydown', keyHandler, true);
  }

  // ------------------------------------------------------------------ per-frame data

  function districtAt(p: V2): District | null {
    if (!layout) return null;
    for (const d of layout.districts) if (rectContains(d.bounds, p)) return d;
    return null;
  }

  function computeSeen(dt: number): boolean {
    const heatSvc = (ctx.services as unknown as { tryGet(k: string): { seen?: boolean } | undefined }).tryGet('heat');
    if (heatSvc && typeof heatSvc.seen === 'boolean') return heatSvc.seen;
    seenTimer -= dt;
    if (seenTimer > 0) return seenCache;
    seenTimer = 0.12;
    seenCache = false;
    if (ctx.state.heat <= 0) return false;
    const vehicles = ctx.services.get('vehicles');
    for (const v of vehicles.query(playerPos, 65)) {
      if (!v.isPolice || v.destroyed || v.controller !== 'ai') continue;
      if (ctx.physics.lineOfSight(v.pos, playerPos)) {
        seenCache = true;
        break;
      }
    }
    return seenCache;
  }

  function gatherMarkers(heat: number): void {
    markers.length = 0;
    for (const src of markerSources) src(markers);
    const vehicles = ctx.services.get('vehicles');
    // Your cars (ownCar.ts): the motor you keep and the last car you drove, never the one you sit in.
    const player = ctx.services.tryGet('player');
    if (player) {
      const driving = player.mode === 'driving' ? player.vehicle : null;
      if (driving) lastDriven = driving.id;
      pushCarMarkers(pickOwnCars(vehicles.all(), driving, lastDriven, !!ctx.services.tryGet('net')?.online, playerPos), markers);
    }
    for (const v of vehicles.query(playerPos, 130)) {
      if (!v.isPolice || v.destroyed) continue;
      const d = Math.hypot(v.pos.x - playerPos.x, v.pos.z - playerPos.z);
      if (heat <= 0 && d > 45) continue;
      markers.push({ x: v.pos.x, z: v.pos.z, kind: 'police', alert: v.siren, pin: false });
    }
  }

  function projectPrompt(prompt: { prompt: string; pos: V2 } | null): HudFrame['prompt'] {
    if (!prompt || !ctx.render || ctx.state.mode !== 'playing' || screenName !== 'hud') return null;
    const c = ctx.camera;
    c.updateMatrixWorld();
    projVec.set(prompt.pos.x, 2.1, prompt.pos.z).project(c);
    const { w, h } = cssSize(ctx.render.canvas);
    return {
      text: prompt.prompt,
      sx: ((projVec.x + 1) / 2) * w,
      sy: ((1 - projVec.y) / 2) * h - 4,
      key: ctx.input.lastDevice === 'gamepad' ? 'Y' : touchUi.enabled ? 'USE' : 'E',
    };
  }

  function updateFlyover(dt: number): void {
    const rig = ctx.services.tryGet('cameraRig');
    if (!rig || !layout) return;
    const b = layout.bounds;
    const cx = (b.minX + b.maxX) / 2;
    const cz = (b.minZ + b.maxZ) / 2;
    const rx = (b.maxX - b.minX) * 0.32;
    const rz = (b.maxZ - b.minZ) * 0.32;
    const t = time * 0.55;
    void dt;
    rig.setOverride({
      target: { x: cx + Math.sin(t * 0.21) * rx, z: cz + Math.sin(t * 0.14 + 1.3) * rz },
      distance: 82,
      pitchDeg: 46,
      yawOffsetDeg: Math.sin(t * 0.09) * 22,
      blend: 1.4,
    });
  }

  // ------------------------------------------------------------------ system

  return {
    id: 'ui',
    order: 750,
    init(c) {
      ctx = c;
      layout = c.services.tryGet('layout') ?? null;
      if (!c.uiRoot || typeof document === 'undefined') {
        c.services.provide('ui', nullService);
        return;
      }
      root = el('div', 'bh-ui', undefined, c.uiRoot);
      root.style.pointerEvents = 'none';
      // `?lockstep` playtests step the sim instantly between screenshots: the HUD's wall-clock state fades
      // (the cinematic beats' 0.4 s, the letterbox bars, pause) land at once there (ui.css, `.bh-lockstep`).
      if (typeof location !== 'undefined' && new URLSearchParams(location.search).has('lockstep')) root.classList.add('bh-lockstep');
      root.insertAdjacentHTML(
        'afterbegin',
        `<svg width="0" height="0" style="position:absolute" aria-hidden="true"><defs>
          <filter id="bh-rough" x="-5%" y="-5%" width="110%" height="110%">
            <feTurbulence type="fractalNoise" baseFrequency="0.045" numOctaves="3" seed="7" result="n"/>
            <feDisplacementMap in="SourceGraphic" in2="n" scale="3.2"/>
          </filter></defs></svg>`,
      );
      hud = new Hud(root);
      hud.setVisible(false);
      // A tap or a click on the minimap opens the full map (MAP / M do the same) — not while a thumb is
      // on the stick or a button, so a stray touch near the map in a chase never pulls the map up.
      hud.onMapTap = () => {
        if (!started || ctx.state.mode !== 'playing' || overlay || activeResult) return false;
        const tc = c.services.tryGet('touchControls');
        if (tc && tc.enabled && tc.fingersDown > 0) return false;
        toggleFullMap();
        return true;
      };
      if (layout) {
        minimap = new Minimap(layout);
        minimap.bake();
      }
      try {
        hiddenGroups = parseHidden(localStorage.getItem(MAP_HIDE_KEY));
      } catch {
        /* no storage */
      }
      minimap?.setHidden(hiddenKinds(hiddenGroups));
      c.services.provide('ui', service);

      // Events -> presentation.
      offs.push(
        c.events.on('vehicleEntered', (e) => {
          if (e.byPlayer) lastDriven = e.vehicleId;
        }),
        c.events.on('missionCompleted', (e) => {
          const meta = missionMeta.get(e.id);
          if (meta?.selfPresented) return;
          void enqueueResult({
            kind: 'success',
            title: meta?.title ?? e.id,
            subtitle: meta?.client ? `${meta.client} is pleased. Nobody saw a thing.` : 'Clean work.',
            lines: [{ label: 'Payment', amount: e.reward }],
          });
        }),
        c.events.on('missionFailed', (e) => {
          if (missionMeta.get(e.id)?.selfPresented) return;
          const f = failStamp(e.reason);
          if (f.kind !== 'failed') {
            // NICKED / WASTED come from the economy's outcome flow; only fall back if it never shows up.
            pendingFail = { id: e.id, reason: e.reason, t: 0.6 };
            return;
          }
          const meta = missionMeta.get(e.id);
          void enqueueResult({ kind: 'failed', title: meta?.title ?? e.id, subtitle: e.reason });
        }),
        c.events.on('playerOutcome', (e) => {
          // Online the server's consequences (fine, seizure, dropped goods) are presented by src/online-ui.
          if (c.services.tryGet('net')?.online) return;
          pendingFail = null;
          const lines = [];
          if (e.fine > 0) lines.push({ label: e.kind === 'busted' ? 'Fine & bail' : 'Hospital bill', amount: -e.fine });
          if (e.waived) lines.push({ label: 'Fine let off — first offence', amount: 0 });
          if (e.vehicleLost) lines.push({ label: 'Motor impounded', amount: 0 });
          const flavour =
            e.kind === 'busted'
              ? ['The law caught up with you.', 'A night in the cells and a lecture.', 'Sergeant says you’ll be out by morning.']
              : ['That’s going to leave a mark.', 'The ambulance took its time.', 'You won’t be doing that again.'];
          void enqueueResult({ kind: e.kind, subtitle: flavour[Math.floor(Math.random() * flavour.length)]!, lines });
        }),
        c.events.on('heatChanged', () => {
          tracker.update(0, c.state.heat, false, playerPos);
        }),
        // A copper saw a first slip while you weren't wanted (docs/POLICE.md §1): a shout, not a chase.
        c.events.on('policeWarning', (e) => {
          const lines = WARNING_LINES[e.type] ?? WARNING_LINES.vehicleDamage!;
          service.say('Constable', lines[warnings++ % lines.length]!, 2.6);
          service.toast('The police saw that. Do it again and they give chase.', 'info', 3.2);
        }),
        // A unit chasing you crashed out by its own driving (docs/POLICE.md §14): stamp, radio, plain toast.
        c.events.on('unitDown', (e) => {
          hud?.unitDown(e.downs, e.needed, e.dropped);
          const lines = e.searchNow ? UNIT_DOWN_LINES.lost : UNIT_DOWN_LINES.still;
          service.say('Police radio', lines[unitDowns++ % lines.length]!, 2.4);
          service.toast(unitDownToast(e), 'good', 3.4, 'urgent'); // news about the chase itself: through in action
        }),
        // A tyre gone (docs/POLICE-AI-POLISH.md): the car pulls and slides; say so plainly.
        c.events.on('vehiclePunctured', (e) => {
          const pv = c.services.tryGet('player')?.vehicle;
          if (!pv || pv.id !== e.vehicleId) return;
          const text = e.cause === 'stinger' ? `Stinger! ${e.flats === 1 ? 'A tyre' : `${e.flats} tyres`} gone — it will pull.` : e.flats >= 3 ? 'Another tyre out. It’s on the rims now.' : 'Tyre shot out! Keep it straight.';
          service.toast(text, 'bad', 3);
        }),
        c.events.on('searchStarted', (e) => policeRadio('search', e.center.x, e.center.z)),
        c.events.on('sightingReported', (e) => policeRadio('tip', e.pos.x, e.pos.z)),
        c.events.on('roadblockSpawned', (e) => policeRadio('roadblock', e.pos.x, e.pos.z)),
        c.events.on('policeSpotted', (e) => {
          // Only a RE-sighting out of a search gets a shout; the first one is the stars lighting up.
          if (heatPhase === 'search') policeRadio('resight', e.pos.x, e.pos.z);
        }),
        c.events.on('crime', (e) => {
          if (e.byPlayer && (e.type === 'firearmDischarge' || e.type === 'shooting' || e.type === 'assault')) sinceCombat = 0;
        }),
        c.events.on('playerHurt', () => {
          sinceCombat = 0;
          if (hitFlashCooldown <= 0) {
            hud?.hitFlash();
            hitFlashCooldown = 0.3;
          }
        }),
        c.events.on('collision', (e) => {
          const veh = c.services.tryGet('player')?.vehicle;
          if (!veh || hitFlashCooldown > 0 || e.speed < 9) return;
          if ((e.a.kind === 'vehicle' && e.a.id === veh.id) || (e.b?.kind === 'vehicle' && e.b.id === veh.id)) {
            hud?.hitFlash();
            hitFlashCooldown = 0.4;
          }
        }),
        c.events.on('settingsChanged', (e) => applySettings(e.settings)),
        // A touch-screen laptop switches the touch UI on at the first finger: re-apply the graphics profile.
        touchUi.onChange(() => applyGraphics(c.services.get('save').settings.graphics)),
        c.events.on('pauseChanged', (e) => {
          if (!started) return;
          // A result sheet is up: it owns the screen, so ignore pause requests (e.g. gamepad Start).
          if (e.paused && (activeResult || overlay)) {
            queueMicrotask(() => c.hooks.call('pause', false));
            return;
          }
          if (e.paused) openPause();
          else closePause();
        }),
        c.events.on('radioChanged', (e) => {
          if (started) hud?.toast(e.name ? `♪ ${e.name} — ${e.tagline}` : '♪ Radio off', 'info', 2.2);
          if (e.index !== c.services.get('save').settings.radioStation) c.services.get('save').updateSettings({ radioStation: e.index });
        }),
        c.events.on('resprayed', () => hud?.hitFlash()),
      );

      installKeys();

      // Hooks for Playwright / console.
      c.hooks.register('uiState', () => ({
        screen: screenName,
        overlay: overlayName,
        started,
        attention: attention.mode,
        mode: c.state.mode,
        hud: hud?.state() ?? null,
        objectiveKind: jobObjective ? 'job' : idleObjective ? 'next' : null,
        resultsQueued: results.length,
        resultActive: activeResult?.info.kind ?? null,
        letterbox: letterboxOn,
        uiMs: +uiMs.toFixed(3),
        // the maps' route line and your cars (ownCar.ts), for the map specs
        routeLine: currentRoute()?.count ?? 0,
        cars: markers.filter((m) => m.kind === 'car').map((m) => ({ x: +m.x.toFixed(1), z: +m.z.toFixed(1), motor: m.glyph === '★', label: m.label })),
        hidden: [...hiddenGroups],
        // the map's look and the bytes its baked bitmaps take (mapStyles.ts)
        mapLook: minimap?.look ?? null,
        mapBytes: minimap?.bakedBytes ?? 0,
      }));
      c.hooks.register('uiNav', (a) => route(a as NavAction));
      c.hooks.register('startGame', (kind) => {
        if (!started) startGame(kind === 'new' ? 'new' : 'continue');
      });
      c.hooks.register('uiResult', (kind, title) => {
        const k = kind as ResultInfo['kind'];
        void enqueueResult(
          k === 'success'
            ? { kind: k, title: title ?? 'The Minnow Run', subtitle: 'Clean work. Nobody saw a thing.', lines: [{ label: 'Payment', amount: 600 }, { label: 'Clean getaway bonus', amount: 150 }] }
            : k === 'busted'
              ? { kind: k, subtitle: 'The law caught up with you.', lines: [{ label: 'Fine & bail', amount: -120 }, { label: 'Motor impounded', amount: 0 }] }
              : k === 'wasted'
                ? { kind: k, subtitle: 'That’s going to leave a mark.', lines: [{ label: 'Hospital bill', amount: -60 }] }
                : { kind: 'failed', title: title ?? 'The Minnow Run', subtitle: 'The motor went up in flames.' },
        );
      });
      c.hooks.register('uiToast', (text, kind) => service.toast(text, (kind as ToastKind | undefined) ?? 'info'));
      c.hooks.register('uiHudOnline', (patch) => service.hudOnline(patch as Partial<HudOnline>));
      c.hooks.register('uiObjective', (title, text, extra) => service.setObjective({ title, text, ...(extra as Partial<ObjectiveInfo> | undefined) }));
      c.hooks.register('uiIntro', (title, client, blurb, reward) => {
        void service.showMissionIntro({ id: 'debug', title, client, blurb, reward, tag: 'JOB 01 · VEHICLE' });
      });
      c.hooks.register('uiScreen', (name) => {
        if (name === 'pause') c.hooks.call('pause', true);
        else if (name === 'map') toggleFullMap();
        else if (name === 'settings') {
          if (!started) openSettings();
          else {
            c.hooks.call('pause', true);
            openSettings();
          }
        } else if (name === 'controls') {
          if (!started) openControls();
          else {
            c.hooks.call('pause', true);
            openControls();
          }
        } else if (name === 'help') {
          service.showScreen('help');
        } else if (name === 'hud') {
          forceCloseModule();
          closeFullMap();
          c.hooks.call('pause', false);
        }
      });
      c.hooks.registerSnapshot('ui', () => ({ screen: screenName, started }));
    },
    start() {
      if (!root) return;
      const interactions = ctx.services.tryGet('interactions');
      interactions?.setBuiltInPrompt(false);
      patchShake();
      applySettings(ctx.services.get('save').settings);
      showTitle();
      const params = new URLSearchParams(location.search);
      // Automated browsers (Playwright) drive the game through __bh and expect gameplay straight away;
      // `?title` forces the title screen for the UI specs.
      const automated = typeof navigator !== 'undefined' && navigator.webdriver === true && !params.has('title');
      if (automated || SKIP_TITLE_PARAMS.some((p) => params.has(p))) {
        // Developer / test entry: straight into the game.
        queueMicrotask(() => {
          if (!started) startGame('continue');
        });
      }
    },
    update(dt) {
      const t0 = performance.now();
      frameUpdate(dt);
      uiMs += (performance.now() - t0 - uiMs) * 0.05;
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
      if (keyHandler) window.removeEventListener('keydown', keyHandler, true);
      overlay?.dispose();
      intro?.dispose();
      hud?.dispose();
      root?.remove();
    },
  };

  function frameUpdate(dt: number): void {
    {
      time += dt;
      hitFlashCooldown -= dt;
      toastSoundCooldown -= dt;
      if (!root || !hud) return;
      if (firstUpdate) {
        firstUpdate = false;
        // Game.init() sets mode 'playing' after our start(); take it back for the title.
        if (!started) ctx.state.mode = 'title';
      }

      // A job's result sheet: what came of it, read once the board has moved on (a frame after the sheet).
      if (activeResult?.screen && !activeResult.afterSet) {
        activeResult.afterSet = true;
        if (activeResult.info.kind === 'success' && !activeResult.info.after) activeResult.screen.setAfter(whatCameOfIt());
      }

      // Menu navigation from the gamepad (works while the sim is paused too).
      if (overlay) for (const a of pad.poll(dt)) route(a);
      else if (started) pad.poll(dt);

      // Full-screen map toggle.
      if (started && ctx.input.framePressed('map') && !overlay) toggleFullMap();

      const player = ctx.services.get('player');
      playerPos.x = player.pos.x;
      playerPos.z = player.pos.z;

      if (pendingFail) {
        pendingFail.t -= dt;
        if (pendingFail.t <= 0) {
          const f = failStamp(pendingFail.reason);
          const meta = missionMeta.get(pendingFail.id);
          void enqueueResult({ kind: f.kind, stamp: f.stamp, title: meta?.title ?? pendingFail.id, subtitle: pendingFail.reason });
          pendingFail = null;
        }
      }

      if (!started) {
        updateFlyover(dt);
        return;
      }

      // ---- HUD data
      const state = ctx.state;
      const heat = state.heat;
      // How busy the player is, and what the HUD may do about it (src/ui/attention.ts).
      sinceCombat += dt;
      const mode = attention.update(dt, {
        heat,
        sinceCombat,
        mph: player.mode === 'driving' && player.vehicle ? mph(Math.abs(player.vehicle.speed)) : 0,
        onFoot: player.mode !== 'driving',
        menu: !!overlay || !!activeResult || state.mode !== 'playing',
      });
      const html = document.documentElement;
      if (html.classList.contains('bh-action') !== (mode === 'action')) html.classList.toggle('bh-action', mode === 'action');
      if (html.classList.contains('bh-calm') !== (mode === 'calm')) html.classList.toggle('bh-calm', mode === 'calm');
      hud.setAction(!allowed('objectiveAutoOpen', mode));
      if (mode !== 'action' && actionToasts.length) {
        for (const t of actionToasts.splice(0)) if (time - t.at < HELD_TOAST_S) service.toast(t.text, t.kind, t.seconds, t.nudge ? 'nudge' : undefined);
      }
      heatPhase = ctx.services.tryGet('heat')?.phase ?? 'none';
      const seen = heat > 0 ? computeSeen(dt) : false;
      const info = tracker.update(dt, heat, seen, playerPos);

      streetTimer -= dt;
      if (streetTimer <= 0) {
        streetTimer = 0.25;
        if (layout) {
          const ne = nearestEdge(layout, playerPos);
          streetName = ne && ne.distance < 30 ? ne.edge.name : '';
          const d = districtAt(playerPos) ?? (ne ? layout.districts.find((x) => x.id === ne.edge.districtId) ?? null : null);
          districtName = d?.name ?? '';
          if (d && d.id !== districtId) {
            districtId = d.id;
            // Not during a cinematic beat: the outro's job card owns the middle of the screen, and a job's
            // last leg often ends over a district line (the lockup in Cinder Yards). The district is still
            // taken as seen, so the card does not pop in over the tally once the bars lift.
            if (!hud.cinematic && allowed('district', attention.mode)) {
              hud.showDistrict(d.name, d.character.slice(0, 3).join(' · '));
              audio()?.stinger('districtCard');
            }
          }
        }
      }

      const veh = player.mode === 'driving' ? player.vehicle : null;
      const rig = ctx.services.tryGet('cameraRig');
      const a = audio();
      const economy = ctx.services.tryGet('economy');
      const frame: HudFrame = {
        dt,
        money: state.money,
        heat,
        seen: info.seen,
        health01: economy ? economy.health / economy.maxHealth : null,
        vehicle: veh
          ? {
              name: veh.spec.displayName,
              mph: mph(veh.speed),
              health01: Math.max(0, veh.health / veh.spec.maxHealth),
              gear: veh.telemetry.reversing ? -1 : veh.speed < 0.6 ? 0 : veh.telemetry.gear,
              burning: veh.condition === 'burning',
              radio: a && a.radio.current >= 0 ? a.radio.nowPlaying || a.radio.stations[a.radio.current]?.name || '' : '',
              radioNews: !!a && a.radio.current >= 0 && !!a.radio.bulletin,
            }
          : null,
        street: streetName || 'Back streets',
        district: districtName,
        prompt: projectPrompt(ctx.services.tryGet('interactions')?.current ?? null),
      };
      hud.update(frame);

      // ---- minimap
      gatherMarkers(heat);
      let search: SearchArea | null = null;
      if (searchOverride !== undefined) search = searchOverride;
      else {
        const hs = (ctx.services as unknown as { tryGet(k: string): { searchArea?: SearchArea | null } | undefined }).tryGet('heat');
        if (hs && hs.searchArea !== undefined) search = hs.searchArea;
        else if (heat > 0 && info.lastSeen) search = { x: info.lastSeen.x, z: info.lastSeen.z, radius: info.radius };
      }
      if (minimap) {
        const driving = !!veh;
        // The minimap turns with the CAMERA, never with the car: the camera keeps north up, so a map that
        // spun to the car's heading stood upside down (felt mirrored) next to the street on screen whenever
        // you drove south. The car is the arrow that turns.
        const targetHeading = rig?.yaw ?? Math.PI;
        viewHeading = lerpAngle(viewHeading, targetHeading, 1 - Math.exp(-7 * dt));
        const targetR = driving ? Math.min(150, 82 + veh!.speed * 1.5) : 68;
        viewRadius += (targetR - viewRadius) * (1 - Math.exp(-2.5 * dt));
        const view: MapView = { x: playerPos.x, z: playerPos.z, heading: viewHeading, radius: viewRadius };
        // In a car the arrow takes its paint: the arrow IS your car (ownCar.ts).
        mapPlayer.x = playerPos.x;
        mapPlayer.z = playerPos.z;
        mapPlayer.heading = player.heading;
        if (driving && veh!.paint !== tintPaint) {
          tintPaint = veh!.paint;
          tintCss = paintCss(tintPaint);
        }
        mapPlayer.tint = driving ? tintCss : null;
        const routeNow = currentRoute();
        minimap.draw(hud.mapCanvas, view, mapPlayer, markers, search, time, routeNow);
        if (fullMap) {
          // The key follows what's on the map (twice a second); the map redraws only when something changed.
          legendTimer -= dt;
          if (legendTimer <= 0) {
            legendTimer = 0.5;
            fullMap.setLegend(mapLegend());
          }
          if (fullMap.wantsDraw(dt)) {
            fullPlayer.x = playerPos.x;
            fullPlayer.z = playerPos.z;
            fullPlayer.heading = player.heading;
            fullPlayer.tint = mapPlayer.tint;
            const wp = ctx.services.tryGet('onboarding')?.waypoint ?? null;
            minimap.drawFull(fullMap.canvas, fullMap.cam, fullMap.dpr, fullPlayer, markers, search, time, wp, fullMap.flash, fullMap.pending, routeNow, fullMap.reserved);
          }
        }
      }
    }
  }
}
